import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxOption } from '@onyx/angular';
import { OnyxLanguagePipe } from '@onyx/angular';
import { chain, groupBy, uniqBy } from 'lodash';
import { ValidationSeverity } from '../../../../common/enums/validation-severity';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import {
  ValidationConditionsHelper,
  ValidationContext,
} from '../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { TelematicsItem } from '../../../../common/interfaces/telematics/telematics-item';
import type { TelematicsRoutePoint } from '../../../../common/interfaces/telematics/telematics-route-point';
import type { ValidationIssue } from '../../../../common/interfaces/validation/validation-issue';
import type { ValidationResult } from '../../../../common/interfaces/validation/validation-result';
import { JoinPipe } from '../../../../common/pipes/join.pipe';
import { DelegationStatus } from '../../../delegations/common/enums/delegation-status';
import type { Driver } from '../../../drivers/common/interfaces/driver';
import type { CompanyVehicleParameter } from '../../../management-panel/dictionaries/common/interfaces/company-vehicle-parameter';
import { SwapSetAction } from '../../../map/common/enums/swap/swap-set-action';
import type { SwapSetData } from '../../../map/common/interfaces/swap/swap-set';
import { OrderPointStatus } from '../../../orders/common/enums/order-point-status';
import { OrderModalHelper } from '../../../orders/common/helpers/order-modal.helper';
import type { Order } from '../../../orders/common/interfaces/order';
import { FleetCategory } from '../enums/fleet-category';
import { SemiTrailerSize } from '../enums/semi-trailer-size';
import { TrailerSize } from '../enums/trailer-size';
import type { Fleet, SimplifiedFleet } from '../interfaces/fleet';
import { FleetHelper } from './fleet.helper';

@Service()
export class FleetValidationHelper {
  private readonly translateService = inject(TranslateService);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );
  private readonly orderModalHelper = inject(OrderModalHelper);
  private readonly joinPipe = inject(JoinPipe);
  private readonly onyxLanguagePipe = inject(OnyxLanguagePipe);

  private readonly I18N = 'issues';

  public validateSets(data: {
    context: ValidationContext;
    primary: Fleet;
    options: {
      warningsFocus?: 'vehicle' | 'trailer';
      secondary: Fleet | SimplifiedFleet;
      driver?: Driver;
      secondaryDriver?: Driver;
      activeOrders?: SwapSetData<Order>[];
      trailerRoutePoints?: TelematicsRoutePoint[];
      option?: OnyxOption<Fleet>;
    };
  }): ValidationResult<Fleet> {
    const { primary, options, context } = data;
    const {
      warningsFocus = 'vehicle',
      secondary,
      option,
      driver,
      secondaryDriver,
      activeOrders,
      trailerRoutePoints,
    } = options;
    const issues: ValidationIssue[] = [];

    const fleet = warningsFocus === 'vehicle' ? primary : secondary;
    if (FleetHelper.isFleetType(secondary) && FleetHelper.isFleetType(fleet)) {
      this.checkExpiryStatuses(fleet, issues, context);
      this.checkIsForSale(fleet, issues);
      this.validationConditionsHelper.checkSetAllowedCountries(
        primary,
        secondary,
        issues,
        context,
      );
      this.validationConditionsHelper.checkCountries(context, issues, {
        driver,
        secondaryDriver,
        vehicle: primary,
        trailer: secondary,
      });

      if (activeOrders?.length) {
        this.checkTrailerOrderIssues(fleet, issues, activeOrders);
      }

      if (context !== ValidationContext.SWAP_PANEL) {
        this.validationConditionsHelper.checkActiveDelegation(fleet, issues);

        for (const driverItem of [driver, secondaryDriver]) {
          if (!driverItem) continue;

          this.validationConditionsHelper.checkRequiredDrivingLicense(
            context,
            driverItem,
            issues,
            { vehicle: primary, trailer: secondary },
          );
          this.validationConditionsHelper.checkDrivingLicenseExpiryDate(
            context,
            driverItem,
            issues,
            { vehicle: primary, trailer: secondary },
          );
        }
      }
    }

    this.checkTrailerCompatibility(context, primary, secondary, issues);

    if (trailerRoutePoints) {
      this.checkIsTrailerLoaded(trailerRoutePoints, issues);
    }

    return ValidationHelper.mapIssues(
      uniqBy(issues, (issue) => issue.message),
      option,
    );
  }

  public validateOrderManualSelect(data: {
    context: ValidationContext;
    order: Order;
    vehicle: Fleet;
    trailer: Fleet | SimplifiedFleet | null;
    hasTrailerConflict: boolean;
    option?: OnyxOption<Fleet>;
  }): ValidationResult<Fleet> {
    const { context, order, vehicle, trailer, hasTrailerConflict, option } =
      data;
    const issues: ValidationIssue[] = [];

    this.checkIsSearchingVehicleValid(order, vehicle, trailer, issues);
    this.checkIsSearchingTrailerValid(order, trailer, issues);
    this.checkTrailerConflict(hasTrailerConflict, issues);
    this.checkExpiryStatuses(vehicle, issues, context);
    this.checkDriverAndDelegation(vehicle, issues);
    this.checkManualProposal(vehicle, issues);

    return ValidationHelper.mapIssues(
      uniqBy(issues, (issue) => issue.message),
      option,
    );
  }

  public validateOrderFormSemiTrailer(data: {
    trailer: Fleet;
    loadedSemiTrailerUuid: string | null;
    isDedicated: boolean;
    option?: OnyxOption<Fleet>;
  }): ValidationResult<Fleet> {
    const { trailer, loadedSemiTrailerUuid, isDedicated, option } = data;
    const issues: ValidationIssue[] = [];

    if (loadedSemiTrailerUuid !== trailer.uuid) {
      if (isDedicated) this.checkIsSemiTrailerLoaded(trailer, issues);
      this.checkIsSemiTrailerAssigned(trailer, issues);
    }

    return ValidationHelper.mapIssues(issues, option);
  }

  public validateIntegrationVehicle(data: {
    vehicle: Fleet;
    ignoreVehicle?: string | null;
    option?: OnyxOption<Fleet>;
  }): ValidationResult<Fleet> {
    const { vehicle, ignoreVehicle, option } = data;
    const issues: ValidationIssue[] = [];

    if (ignoreVehicle !== vehicle.uuid) {
      this.checkHasTelematics(vehicle, issues);
    }

    return ValidationHelper.mapIssues(issues, option);
  }

  public validateFleet(data: {
    context: ValidationContext;
    primary: Fleet;
    options: {
      driver?: Driver;
      secondaryDriver?: Driver;
      option?: OnyxOption<Fleet>;
    };
  }): ValidationResult<Fleet> {
    const { primary, options, context } = data;
    const { option, driver, secondaryDriver } = options;
    const issues: ValidationIssue[] = [];

    this.checkExpiryStatuses(primary, issues, context);
    this.validationConditionsHelper.checkCountries(context, issues, {
      driver,
      vehicle: primary,
      secondaryDriver,
    });
    this.checkIsForSale(primary, issues);

    for (const driverItem of [driver, secondaryDriver]) {
      if (!driverItem) continue;

      this.validationConditionsHelper.checkRequiredDrivingLicense(
        context,
        driverItem,
        issues,
        { vehicle: primary },
      );
      this.validationConditionsHelper.checkDrivingLicenseExpiryDate(
        context,
        driverItem,
        issues,
        { vehicle: primary },
      );
    }

    return ValidationHelper.mapIssues(
      uniqBy(issues, (issue) => issue.message),
      option,
    );
  }

  public validateDriver(data: {
    context: ValidationContext;
    vehicle: Fleet;
    options: {
      driver: Driver;
      option?: OnyxOption<Fleet>;
    };
  }) {
    const { vehicle, options, context } = data;
    const { driver, option } = options;
    const issues: ValidationIssue[] = [];

    this.validationConditionsHelper.checkSkills(driver, issues, vehicle);
    this.checkIsFullCrew(vehicle, issues);
    this.validationConditionsHelper.checkAdr(driver, issues, vehicle);
    this.checkIsForSale(vehicle, issues);
    this.checkExpiryStatuses(vehicle, issues, context);
    this.validationConditionsHelper.checkCountries(context, issues, {
      driver,
      vehicle,
    });
    this.validationConditionsHelper.checkRequiredDrivingLicense(
      context,
      driver,
      issues,
      { vehicle },
    );
    this.validationConditionsHelper.checkDrivingLicenseExpiryDate(
      context,
      driver,
      issues,
      { vehicle },
    );
    this.validationConditionsHelper.checkActiveDelegation(vehicle, issues);

    return ValidationHelper.mapIssues(issues, option);
  }

  public checkTrailerCompatibility(
    context: ValidationContext,
    primary: Fleet | TelematicsItem,
    secondary: Fleet | SimplifiedFleet,
    issues: ValidationIssue[],
  ): void {
    const category = primary.generalInformation.category;
    const { isLowDeck } =
      'additionalParameters' in primary
        ? primary.additionalParameters
        : primary.parameters;
    const { semiTrailerSize, trailerSize } = secondary.additionalParameters;

    const errors = [
      {
        condition:
          isLowDeck &&
          category === FleetCategory.SEMI_TRUCK &&
          semiTrailerSize !== SemiTrailerSize.MEGA,
        message: `${this.I18N}.standardTrailer`,
      },
      {
        condition:
          !isLowDeck &&
          category === FleetCategory.SEMI_TRUCK &&
          semiTrailerSize === SemiTrailerSize.MEGA,
        message: `${this.I18N}.lowDeck`,
      },
      {
        condition:
          category === FleetCategory.VAN && trailerSize !== TrailerSize.VAN,
        message: `${this.I18N}.vanTrailer`,
      },
      {
        condition:
          category === FleetCategory.STRAIGHT_TRUCK &&
          trailerSize !== TrailerSize.STRAIGHT_TRUCK,
        message: `${this.I18N}.straightTruckTrailer`,
      },
    ];

    for (const { condition, message } of errors) {
      if (!condition) continue;

      issues.push({
        severity:
          context === ValidationContext.SWAP_PANEL
            ? ValidationSeverity.WARNING
            : ValidationSeverity.ERROR,
        message,
      });
    }
  }

  public checkExpiryStatuses(
    fleet: Fleet,
    issues: ValidationIssue[],
    context: ValidationContext,
  ): void {
    const statuses = FleetHelper.getVehicleExpiryStatuses(fleet);
    if (!statuses) return;

    this.validationConditionsHelper.addExpiryIssue(
      statuses.errors,
      statuses.errors.length
        ? `<span class='f-body-s f-semibold'>${this.translateService.instant(`labels.${statuses.errors[0].key}`)}</span> ${this.translateService.instant('fleet.fleetModal.expired')}`
        : '',
      `${this.I18N}.documents.documentsExpired`,
      issues,
      {
        forceSeverity: {
          [ValidationContext.DELEGATION]: ValidationSeverity.ERROR,
          [ValidationContext.SET]: ValidationSeverity.WARNING,
          [ValidationContext.DRIVER]: ValidationSeverity.WARNING,
          [ValidationContext.FLEET]: ValidationSeverity.WARNING,
          [ValidationContext.ORDER]: ValidationSeverity.WARNING,
          [ValidationContext.SWAP_PANEL]: ValidationSeverity.WARNING,
        }[context],
      },
    );
    this.validationConditionsHelper.addExpiryIssue(
      statuses.warnings,
      statuses.warnings.length
        ? `<span class='f-body-s f-semibold'>${this.translateService.instant(`labels.${statuses.warnings[0].key}`)}</span> ${this.translateService.instant('fleet.fleetModal.expires')}`
        : '',
      `${this.I18N}.documents.documentsExpires`,
      issues,
      { forceSeverity: ValidationSeverity.WARNING },
    );
  }

  private checkIsFullCrew(fleet: Fleet, issues: ValidationIssue[]): void {
    const drivers = chain(fleet.drivers).values().compact().value();
    if (drivers.length !== 2) return;

    issues.push({
      severity: ValidationSeverity.WARNING,
      message: `${this.I18N}.fullCrew`,
    });
  }

  private checkIsForSale(fleet: Fleet, issues: ValidationIssue[]): void {
    if (!fleet.ownership.isForSale) return;

    issues.push({
      severity: ValidationSeverity.WARNING,
      message: `${this.I18N}.vehicleForSale`,
    });
  }

  private checkIsSearchingVehicleValid(
    order: Order,
    vehicle: Fleet,
    trailer: Fleet | SimplifiedFleet | null,
    issues: ValidationIssue[],
  ): void {
    const vehicleParameters =
      this.orderModalHelper.getSearchingVehicleParameters(
        order,
        vehicle,
        trailer,
      );

    const invalidParameters = vehicleParameters.filter((param) => !param.ok());
    if (!invalidParameters.length) return;

    const groupedParameters = groupBy(invalidParameters, (parameter) =>
      parameter.key === 'other' ? 'other' : 'base',
    );
    const baseNames = (groupedParameters['base'] ?? []).map(
      (parameter) => parameter.name,
    );
    const otherNames = (groupedParameters['other'] ?? []).flatMap((parameter) =>
      (parameter.value as CompanyVehicleParameter[]).map((parameter) =>
        this.onyxLanguagePipe.transform(parameter.names),
      ),
    );

    const message = `${this.translateService.instant('labels.vehicleDoesNotMeetRequirements')}: ${this.joinPipe.transform([...baseNames, ...otherNames])}`;

    issues.push({
      severity: ValidationSeverity.WARNING,
      message,
    });
  }

  private checkIsSearchingTrailerValid(
    order: Order,
    trailer: Fleet | SimplifiedFleet | null,
    issues: ValidationIssue[],
  ): void {
    if (!trailer) return;

    const trailerParameters =
      this.orderModalHelper.getSearchingTrailerParameters(order, trailer);
    const invalidParameters = trailerParameters.filter((param) => !param.ok);
    if (!invalidParameters.length) return;

    const message = `${this.translateService.instant(`labels.${trailer.generalInformation.category === FleetCategory.SEMI_TRAILER ? 'semiTrailer' : 'trailer'}DoesNotMeetRequirements`)}: ${this.joinPipe.transform(
      invalidParameters.map((param) => param.name),
    )}`;

    issues.push({
      severity: ValidationSeverity.WARNING,
      message,
    });
  }

  private checkTrailerConflict(
    hasTrailerConflict: boolean,
    issues: ValidationIssue[],
  ): void {
    if (!hasTrailerConflict) return;

    issues.push({
      severity: ValidationSeverity.ERROR,
      message: 'labels.orderHasLoadedSemiTrailer',
    });
  }

  private checkDriverAndDelegation(
    fleet: Fleet,
    issues: ValidationIssue[],
  ): void {
    if (
      !fleet.drivers?.primaryDriver ||
      fleet.delegation?.status.value !== DelegationStatus.ACTIVE
    ) {
      issues.push({
        severity: ValidationSeverity.ERROR,
        message: `${this.I18N}.driverAndDelegationRequired`,
      });
    }
  }

  private checkManualProposal(fleet: Fleet, issues: ValidationIssue[]): void {
    if (!fleet.manualProposalOrderUuid) return;

    issues.push({
      severity: ValidationSeverity.ERROR,
      message: `${this.I18N}.manualProposal`,
    });
  }

  private checkIsTrailerLoaded(
    routePoints: TelematicsRoutePoint[],
    issues: ValidationIssue[],
  ): void {
    const point = routePoints.find(
      (point) => RouteHelper.getStatus(point) === OrderPointStatus.TO_DO,
    );
    if (!point?.order?.identifier) return;

    issues.push({
      severity: ValidationSeverity.ERROR,
      message: `${this.I18N}.semiTrailerLoaded`,
      data: { orderIdentifier: point.order.identifier },
    });
  }

  private checkIsSemiTrailerLoaded(
    trailer: Fleet,
    issues: ValidationIssue[],
  ): void {
    if (!trailer.orderUuid) return;

    issues.push({
      severity: ValidationSeverity.ERROR,
      message: `${this.I18N}.semiTrailerLoadedInOtherOrder`,
    });
  }

  private checkIsSemiTrailerAssigned(
    trailer: Fleet,
    issues: ValidationIssue[],
  ): void {
    if (!trailer.vehicle) return;

    issues.push({
      severity: ValidationSeverity.ERROR,
      message: `${this.I18N}.semiTrailerAssigned`,
    });
  }

  private checkHasTelematics(vehicle: Fleet, issues: ValidationIssue[]): void {
    if (!vehicle.telematics) return;

    issues.push({
      severity: ValidationSeverity.WARNING,
      message: `${this.I18N}.vehicleAlreadyAssigned`,
    });
  }

  private checkTrailerOrderIssues(
    trailer: Fleet | SimplifiedFleet,
    issues: ValidationIssue[],
    activeOrders?: SwapSetData<Order>[],
  ): void {
    if (!activeOrders) return;

    const orderIssues = chain(activeOrders)
      .filter(
        (order) =>
          ![
            SwapSetAction.UNLOAD_IN_POINT,
            SwapSetAction.DROP_OFF_ON_SEMI_TRAILER,
          ].includes(order.action),
      )
      .flatMap(({ value }) =>
        value
          ? this.orderModalHelper.getSearchingTrailerParameters(value, trailer)
          : [],
      )
      .filter((result) => !result.ok())
      .map((result): ValidationIssue => ({
        severity: ValidationSeverity.WARNING,
        message: result.name,
      }))
      .value();

    issues.push(...orderIssues);
  }
}
